import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { budgets, transactions } from '../services/api';
import { useBudget } from './BudgetContext';

export interface TransactionChange {
  type: 'transaction_category';
  transactionId: string;
  oldCategory: string;
  newCategory: string;
  timestamp: number;
}

export interface BudgetAmountChange {
  type: 'budget_amount';
  categoryName: string;
  categoryType: 'income' | 'expense' | 'savings';
  oldAmount: number;
  newAmount: number;
  timestamp: number;
}

export type Change = TransactionChange | BudgetAmountChange;

interface NavigationContextType {
  changeHistory: Change[];
  undoneChanges: Change[];
  updatingTransaction: boolean;
  handleUndo: () => Promise<void>;
  handleRedo: () => Promise<void>;
  setChangeHistory: React.Dispatch<React.SetStateAction<Change[]>>;
  setUndoneChanges: React.Dispatch<React.SetStateAction<Change[]>>;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentBudget, setCurrentBudget } = useBudget();
  const [changeHistory, setChangeHistory] = useState<Change[]>([]);
  const [undoneChanges, setUndoneChanges] = useState<Change[]>([]);
  const [updatingTransaction, setUpdatingTransaction] = useState(false);

  const handleUndo = useCallback(async () => {
    if (changeHistory.length === 0 || updatingTransaction) return;

    try {
      setUpdatingTransaction(true);
      const lastChange = changeHistory[changeHistory.length - 1];
      
      if (lastChange.type === 'transaction_category') {
        // Get the transaction details first
        const transaction = await transactions.getById(lastChange.transactionId);
        
        // Make the API call to update the transaction
        await transactions.update(lastChange.transactionId, {
          category: lastChange.oldCategory
        });

        // Update budget amounts if we have a current budget
        if (currentBudget) {
          const updatedBudget = {
            ...currentBudget,
            categories: currentBudget.categories.map(category => {
              if (category.name === lastChange.newCategory && category.type === transaction.type) {
                // Subtract amount from current category
                return {
                  ...category,
                  actualAmount: category.actualAmount - transaction.amount
                };
              }
              if (category.name === lastChange.oldCategory && category.type === transaction.type) {
                // Add amount back to old category
                return {
                  ...category,
                  actualAmount: category.actualAmount + transaction.amount
                };
              }
              return category;
            })
          };
          
          // Update budget in context
          setCurrentBudget(updatedBudget);
        }

        // Dispatch custom event to trigger transaction refresh
        window.dispatchEvent(new CustomEvent('transactionUndoRedo'));
      } else if (lastChange.type === 'budget_amount') {
        // Update budget amounts
        if (currentBudget) {
          const updatedBudget = {
            ...currentBudget,
            categories: currentBudget.categories.map(category => {
              if (category.name === lastChange.categoryName && category.type === lastChange.categoryType) {
                return {
                  ...category,
                  budgetedAmount: lastChange.oldAmount
                };
              }
              return category;
            }),
            totalBudgeted: currentBudget.totalBudgeted - lastChange.newAmount + lastChange.oldAmount
          };
          
          // Update budget in API and context
          await budgets.update(currentBudget._id, updatedBudget);
          setCurrentBudget(updatedBudget);
        }
      }

      setChangeHistory(prev => prev.slice(0, -1));
      setUndoneChanges(prev => [...prev, lastChange]);
    } catch (err) {
      console.error('Error undoing change:', err);
    } finally {
      setUpdatingTransaction(false);
    }
  }, [changeHistory, updatingTransaction, currentBudget, setCurrentBudget]);

  const handleRedo = useCallback(async () => {
    if (undoneChanges.length === 0 || updatingTransaction) return;

    try {
      setUpdatingTransaction(true);
      const lastUndoneChange = undoneChanges[undoneChanges.length - 1];
      
      if (lastUndoneChange.type === 'transaction_category') {
        // Get the transaction details first
        const transaction = await transactions.getById(lastUndoneChange.transactionId);
        
        // Make the API call to update the transaction
        await transactions.update(lastUndoneChange.transactionId, {
          category: lastUndoneChange.newCategory
        });

        // Update budget amounts if we have a current budget
        if (currentBudget) {
          const updatedBudget = {
            ...currentBudget,
            categories: currentBudget.categories.map(category => {
              if (category.name === lastUndoneChange.oldCategory && category.type === transaction.type) {
                // Subtract amount from old category
                return {
                  ...category,
                  actualAmount: category.actualAmount - transaction.amount
                };
              }
              if (category.name === lastUndoneChange.newCategory && category.type === transaction.type) {
                // Add amount to new category
                return {
                  ...category,
                  actualAmount: category.actualAmount + transaction.amount
                };
              }
              return category;
            })
          };
          
          // Update budget in context
          setCurrentBudget(updatedBudget);
        }

        // Dispatch custom event to trigger transaction refresh
        window.dispatchEvent(new CustomEvent('transactionUndoRedo'));
      } else if (lastUndoneChange.type === 'budget_amount') {
        // Update budget amounts
        if (currentBudget) {
          const updatedBudget = {
            ...currentBudget,
            categories: currentBudget.categories.map(category => {
              if (category.name === lastUndoneChange.categoryName && category.type === lastUndoneChange.categoryType) {
                return {
                  ...category,
                  budgetedAmount: lastUndoneChange.newAmount
                };
              }
              return category;
            }),
            totalBudgeted: currentBudget.totalBudgeted - lastUndoneChange.oldAmount + lastUndoneChange.newAmount
          };
          
          // Update budget in API and context
          await budgets.update(currentBudget._id, updatedBudget);
          setCurrentBudget(updatedBudget);
        }
      }

      setUndoneChanges(prev => prev.slice(0, -1));
      setChangeHistory(prev => [...prev, lastUndoneChange]);
    } catch (err) {
      console.error('Error redoing change:', err);
    } finally {
      setUpdatingTransaction(false);
    }
  }, [undoneChanges, updatingTransaction, currentBudget, setCurrentBudget]);

  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Check if Ctrl/Cmd + Z is pressed
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        
        // Check Ctrl+Shift+Z (Redo) first
        if (e.shiftKey) {
          if (undoneChanges.length > 0 && !updatingTransaction) {
            await handleRedo();
          }
        } else {
          if (changeHistory.length > 0 && !updatingTransaction) {
            await handleUndo();
          }
        }
      }
      // Check if Ctrl/Cmd + Y is pressed (alternative redo shortcut)
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        if (undoneChanges.length > 0 && !updatingTransaction) {
          await handleRedo();
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [changeHistory.length, undoneChanges.length, updatingTransaction, handleUndo, handleRedo]);

  const value = React.useMemo(() => ({
    changeHistory,
    undoneChanges,
    updatingTransaction,
    handleUndo,
    handleRedo,
    setChangeHistory,
    setUndoneChanges
  }), [
    changeHistory,
    undoneChanges,
    updatingTransaction,
    handleUndo,
    handleRedo
  ]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}; 